import { useEffect, useState } from 'react';
import { api } from '../services/api';
import type { StoreConfig } from '../types/api';
import './StoreSettings.css';

interface Props {
  onSaved?: (cfg: StoreConfig) => void;
}

export default function StoreSettings({ onSaved }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    name: '',
    logoUrl: '',
    phone: '',
    commercialMessage: '',
    primaryColor: '#38c738',
  });

  async function handleLogoFileChange(file: File | null) {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Selecione uma imagem valida para o logo');
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('O logo deve ter no maximo 2 MB');
      return;
    }

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Nao foi possivel ler a imagem'));
      reader.readAsDataURL(file);
    });

    setForm(prev => ({ ...prev, logoUrl: dataUrl }));
    setError('');
  }

  useEffect(() => {
    api.getStoreConfig()
      .then(cfg => {
        setForm({
          name: cfg.name,
          logoUrl: cfg.logo,
          phone: cfg.phone,
          commercialMessage: cfg.commercialMessage,
          primaryColor: cfg.primaryColor,
        });
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const cfg = await api.updateStoreConfig(form);
      onSaved?.(cfg);
      setSuccess('Configuracoes salvas com sucesso');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="store-settings-loading">Carregando configuracoes...</div>;
  }

  return (
    <section className="store-settings">
      <div className="store-settings-header">
        <h2>Configuracoes da Empresa</h2>
        <p>Edite nome, logo, telefone, mensagem e cor principal.</p>
      </div>

      <form className="store-settings-form" onSubmit={handleSubmit}>
        <label>
          Nome da empresa
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </label>

        <label>
          Logo (URL)
          <input value={form.logoUrl} onChange={e => setForm({ ...form, logoUrl: e.target.value })} placeholder="/api/assets/logo.png" />
        </label>

        <label>
          Logo (upload)
          <input
            type="file"
            accept="image/*"
            onChange={e => {
              void handleLogoFileChange(e.target.files?.[0] ?? null);
              e.currentTarget.value = '';
            }}
          />
        </label>

        {form.logoUrl && (
          <div className="store-settings-logo-preview">
            <span>Previa do logo</span>
            <img src={form.logoUrl} alt="Previa do logo" />
          </div>
        )}

        <label>
          Telefone
          <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
        </label>

        <label>
          Mensagem comercial
          <textarea value={form.commercialMessage} onChange={e => setForm({ ...form, commercialMessage: e.target.value })} rows={4} />
        </label>

        <label>
          Cor principal
          <input type="color" value={form.primaryColor} onChange={e => setForm({ ...form, primaryColor: e.target.value })} />
        </label>

        {error && <p className="store-settings-error">{error}</p>}
        {success && <p className="store-settings-success">{success}</p>}

        <button className="store-settings-save" type="submit" disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar configuracoes'}
        </button>
      </form>
    </section>
  );
}
